import {
  EligibleContactType,
  HausarztAssertion,
  canonicalHausarzt,
  eligibleContactKind,
  normalizeHausarztWhitespace,
} from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
import type {
  ReviewContact,
  ReviewItemId,
} from "@ascleo/frontdesk-contracts/patient-info/review";
import {
  ContactAssertion,
  type ContactOperation,
} from "@ascleo/frontdesk-contracts/patient-info/submit";
import { Schema } from "effect";

/**
 * A patient-added contact, confirmed valid, awaiting save.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const addition: PendingAddition = {
 *   clientItemId: "add-1",
 *   type: "MOBILEPRIV",
 *   value: "0170 1234567",
 * };
 * ```
 */
export interface PendingAddition {
  readonly clientItemId: string;
  readonly type: EligibleContactType;
  readonly value: string;
}

/**
 * A patient's proposed replacement for a protected contact.
 *
 * A protected contact is never written directly, so a proposal is an ordinary `edit` operation that
 * the server routes to a reception task. Keeping it a separate client-side concept lets the UI say
 * so before the commit, which is the whole point of disclosing routing early.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const proposal: PendingProposal = { itemId: "itm-3", value: "neu@praxis.de" };
 * ```
 */
export interface PendingProposal {
  readonly itemId: ReviewItemId;
  readonly value: string;
}

/**
 * The task-only Hausarzt part of a submit request.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const proposal: HausarztProposal = { changed: true, value: "Praxis Am Markt" }
 * ```
 */
export type HausarztProposal =
  | { readonly changed: false }
  | { readonly changed: true; readonly value: HausarztAssertion | null };

/**
 * Build the submit operations from the form: a deleted ordinary contact → `delete`; a changed value →
 * `edit`. Unchanged, non-deleted contacts produce nothing (so an untouched review is a no-op). A value
 * that does not pass the format assertion is skipped here — the form gates submit on validity, so this
 * only guards against a race and never sends an invalid value.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * buildOperations(contacts, { "itm-0": "neu@praxis.de" }, new Set(), []);
 * // => [{ kind: "edit", itemId: "itm-0", value: "neu@praxis.de" }]
 * ```
 */
export function buildOperations(
  ordinary: readonly ReviewContact[],
  values: Record<string, string>,
  deleted: ReadonlySet<string>,
  additions: readonly PendingAddition[] = [],
  proposals: readonly PendingProposal[] = [],
): readonly ContactOperation[] {
  const operations: ContactOperation[] = [];
  for (const contact of ordinary) {
    if (deleted.has(contact.itemId)) {
      operations.push({ kind: "delete", itemId: contact.itemId });
      continue;
    }
    const next = values[contact.itemId];
    if (next === undefined || next.trim() === contact.value.trim()) continue;
    const decoded = Schema.decodeUnknownResult(ContactAssertion)(next);
    if (decoded._tag === "Success") {
      operations.push({
        kind: "edit",
        itemId: contact.itemId,
        value: decoded.success,
      });
    }
  }
  for (const addition of additions) {
    const decoded = Schema.decodeUnknownResult(ContactAssertion)(addition.value);
    if (decoded._tag === "Success") {
      operations.push({
        kind: "add",
        clientItemId: addition.clientItemId,
        type: addition.type,
        value: decoded.success,
      });
    }
  }
  // A proposal against a protected contact is an ordinary edit on the wire. The server's write
  // strategy is what turns it into a reception task; the client never invents an operation kind.
  for (const proposal of proposals) {
    const decoded = Schema.decodeUnknownResult(ContactAssertion)(proposal.value);
    if (decoded._tag === "Success") {
      operations.push({
        kind: "edit",
        itemId: proposal.itemId,
        value: decoded.success,
      });
    }
  }
  return operations;
}

/**
 * Compare the form value with the review base and return only a genuine, format-valid proposal.
 *
 * Comparison ignores case and whitespace. Empty input means clearing the entry (`null`); invalid or
 * overlong input is omitted as a final submit-race guard because the form already blocks it.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * buildHausarztProposal("Dr. Anna Müller", "Praxis Am Markt")
 * // { changed: true, value: "Praxis Am Markt" }
 * ```
 */
export function buildHausarztProposal(
  current: string | null,
  input: string,
): HausarztProposal {
  const normalized = normalizeHausarztWhitespace(input);
  if (canonicalHausarzt(current ?? "") === canonicalHausarzt(normalized)) {
    return { changed: false };
  }
  if (normalized.length === 0) return { changed: true, value: null };

  const decoded = Schema.decodeUnknownResult(HausarztAssertion)(normalized);
  return decoded._tag === "Success"
    ? { changed: true, value: decoded.success }
    : { changed: false };
}

/**
 * Everything the review derives from what the patient has staged so far.
 *
 * Nothing here is stored: counts, routing, validity, and the primary action's label are all
 * recomputed from the form and the staged sets, so there is no second source of truth to drift.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const staged: StagedSummary = {
 *   operations: [],
 *   directCount: 0,
 *   receptionCount: 0,
 *   totalCount: 0,
 *   perCategory: { phone: 0, email: 0, hausarzt: 0 },
 * };
 * ```
 */
export interface StagedSummary {
  readonly operations: readonly ContactOperation[];
  readonly hausarzt: HausarztProposal;
  readonly directCount: number;
  readonly receptionCount: number;
  readonly totalCount: number;
  readonly perCategory: Readonly<Record<"phone" | "email" | "hausarzt", number>>;
  /**
   * True when the patient has typed something that cannot be sent.
   *
   * Checked independently of which fields are currently mounted. Closing a category card unmounts
   * its inputs and with them their validators, so without this an invalid value could sit in the
   * form, be dropped silently by `buildOperations`, and leave the patient looking at "Alles korrekt"
   * while their typing was discarded.
   */
  readonly hasInvalidInput: boolean;
}

/**
 * The form field name carrying a proposal for a protected contact.
 *
 * Proposals live in the same form as ordinary edits so they inherit its validation and submit
 * gating rather than growing a parallel validity story.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * proposalFieldName("itm-3"); // => "proposal:itm-3"
 * ```
 */
export function proposalFieldName(itemId: string): string {
  return `proposal:${itemId}`;
}

/**
 * Derive the staged summary from the current form values and staged sets.
 *
 * Routing follows the accepted product rule: an ordinary phone or email change is saved directly,
 * while a protected-contact proposal and every Hausarzt change go to reception. The counts this
 * produces are what the balance line and the per-category chips state before the patient commits,
 * so they must match the server's routing rather than a hopeful approximation of it.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * summarizeStagedChanges({
 *   contacts: [],
 *   values: {},
 *   deleted: new Set(),
 *   additions: [],
 *   proposals: [],
 *   currentHausarzt: null,
 *   hausarztInput: "",
 *   hausarztEnabled: false,
 * }).totalCount; // => 0
 * ```
 */
/** True when a non-empty value cannot pass the contract's contact format assertion. */
function isInvalidContactValue(value: string): boolean {
  return (
    value.trim().length > 0 &&
    Schema.decodeUnknownResult(ContactAssertion)(value)._tag === "Failure"
  );
}

export function summarizeStagedChanges(input: {
  readonly contacts: readonly ReviewContact[];
  readonly values: Record<string, string>;
  readonly deleted: ReadonlySet<string>;
  readonly additions: readonly PendingAddition[];
  readonly proposals: readonly PendingProposal[];
  readonly currentHausarzt: string | null;
  readonly hausarztInput: string;
  readonly hausarztEnabled: boolean;
}): StagedSummary {
  const ordinary = input.contacts.filter(
    (contact) => contact.protection === undefined,
  );
  const operations = buildOperations(
    ordinary,
    input.values,
    input.deleted,
    input.additions,
    input.proposals,
  );
  const hausarzt = input.hausarztEnabled
    ? buildHausarztProposal(input.currentHausarzt, input.hausarztInput)
    : ({ changed: false } as const);

  const protectedIds = new Set(
    input.contacts
      .filter((contact) => contact.protection !== undefined)
      .map((contact) => contact.itemId),
  );

  // Validity is evaluated over what the patient typed, not over what happens to be mounted.
  const hasInvalidInput =
    ordinary.some(
      (contact) =>
        !input.deleted.has(contact.itemId) &&
        isInvalidContactValue(input.values[contact.itemId] ?? ""),
    ) ||
    input.proposals.some((proposal) => isInvalidContactValue(proposal.value)) ||
    (input.hausarztEnabled &&
      normalizeHausarztWhitespace(input.hausarztInput).length > 120);
  const typeOf = new Map(
    input.contacts.map((contact) => [contact.itemId, contact.type]),
  );

  const perCategory = { phone: 0, email: 0, hausarzt: 0 };
  let directCount = 0;
  let receptionCount = 0;

  for (const operation of operations) {
    const type =
      operation.kind === "add"
        ? operation.type
        : typeOf.get(operation.itemId);
    if (type !== undefined) perCategory[eligibleContactKind(type)] += 1;

    const routesToReception =
      operation.kind !== "add" && protectedIds.has(operation.itemId);
    if (routesToReception) receptionCount += 1;
    else directCount += 1;
  }

  if (hausarzt.changed) {
    perCategory.hausarzt += 1;
    receptionCount += 1;
  }

  return {
    operations,
    hausarzt,
    directCount,
    receptionCount,
    totalCount: directCount + receptionCount,
    perCategory,
    hasInvalidInput,
  };
}

/**
 * The balance line's parts: the total, then only the routes that actually carry something.
 *
 * A route with nothing on it is omitted rather than printed as zero. "0 prüft der Empfang" is
 * noise at best and, on a screen whose whole job is telling the patient where their change is
 * going, actively misleading.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * balanceParts({ totalCount: 2, directCount: 1, receptionCount: 1 });
 * // => [
 * //   { key: "total", count: 2 },
 * //   { key: "direct", count: 1 },
 * //   { key: "reception", count: 1 },
 * // ]
 * ```
 */
export function balanceParts(
  staged: Pick<StagedSummary, "totalCount" | "directCount" | "receptionCount">,
): readonly { readonly key: "total" | "direct" | "reception"; readonly count: number }[] {
  if (staged.totalCount === 0) return [];
  const parts: { key: "total" | "direct" | "reception"; count: number }[] = [
    { key: "total", count: staged.totalCount },
  ];
  if (staged.directCount > 0) parts.push({ key: "direct", count: staged.directCount });
  if (staged.receptionCount > 0)
    parts.push({ key: "reception", count: staged.receptionCount });
  return parts;
}
